import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { MissionVersion, WorkspaceOperation } from './workspaceApi';
import { RuntimeBudgetPanel } from './RuntimeBudgetPanel';
import { OperationActionReview } from './OperationActionReview';

const actionNames: Record<string, string> = {
  'campaign-summary': 'Read campaign summary',
  'campaign-start': 'Start campaign',
  'campaign-stop': 'Stop campaign (drain)',
  'campaign-rename': 'Rename campaign',
  'webhook-dispatch': 'Dispatch webhook',
};

export const MissionRunList = ({
  rows,
  onReview,
  versions = [],
}: {
  rows: WorkspaceOperation[];
  onReview: (row: WorkspaceOperation) => void;
  versions?: MissionVersion[];
}) => {
  const [selected, setSelected] = useState<WorkspaceOperation | null>(null);
  // Keep an open result current when polling advances an operation.
  const result = rows.find((row) => row.id === selected?.id) ?? selected;
  const version = versions.find((item) => item.id === result?.version_id);
  return (
    <>
      {!!rows.length && (
        <TableContainer component={Box} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table aria-label="Mission run history" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                {['Status', 'Started', 'Actions', 'Result', ''].map((label, index) => (
                  <TableCell key={index}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={row.state} />
                  </TableCell>
                  <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    {row.actions
                      .map((action) => actionNames[action.kind] ?? action.kind)
                      .join(' → ') ||
                      (row.mission_id ? 'No actions executed' : 'Assistant request')}
                  </TableCell>
                  <TableCell>
                    <Stack gap={1}>
                      <Typography variant="body2">
                        {row.result.length
                          ? `${row.result.length} recorded ${row.result.length === 1 ? 'result' : 'results'}`
                          : 'No result recorded yet'}
                      </Typography>
                      {row.error_code && (
                        <Typography
                          variant="caption"
                          color={row.state === 'review' ? 'warning.main' : 'error.main'}>
                          {row.error_code.replaceAll('_', ' ')}
                        </Typography>
                      )}
                      {row.state === 'prepared' && (
                        <Button onClick={() => onReview(row)}>Review saved proposal</Button>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      aria-label={`View result for run ${row.id}`}
                      onClick={() => setSelected(row)}>
                      View result
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog
        open={!!result}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="mission-run-result-title">
        <DialogTitle id="mission-run-result-title">Run result</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {result && (
              <>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip label={result.state} size="small" />
                  <Typography variant="body2">
                    {new Date(result.created_at).toLocaleString()}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}>
                  Run {result.id}
                </Typography>
                {result.error_code && (
                  <Alert severity={result.state === 'review' ? 'warning' : 'error'}>
                    {['effect_requires_review', 'execution_interrupted'].includes(result.error_code)
                      ? 'Execution outcome requires manual review. Inspect the campaign or delivery before starting another run.'
                      : result.error_code.replaceAll('_', ' ')}
                  </Alert>
                )}
                <Typography variant="body2">
                  {result.actions
                    .map((action) => actionNames[action.kind] ?? action.kind)
                    .join(' → ') ||
                    (result.mission_id ? 'No actions executed' : 'Assistant request')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Recorded configuration</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      {result.version_id
                        ? version
                          ? `Published version ${version.number} · ${result.version_id}`
                          : `Published version ID: ${result.version_id}`
                        : 'Reviewed assistant proposal'}
                    </Typography>
                    {version && (
                      <>
                        <Typography variant="body2">{version.spec.name}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {version.spec.brief}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Trigger: {version.spec.trigger.kind}
                          {version.spec.trigger.cron
                            ? ` · ${version.spec.trigger.cron} · ${version.spec.trigger.timezone}`
                            : ''}
                        </Typography>
                      </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      These actions were saved for this run. Later draft edits do not change them.
                    </Typography>
                    <OperationActionReview actions={result.actions} snapshots={result.snapshots} />
                  </Stack>
                </Paper>
                {result.runtimeBudget && <RuntimeBudgetPanel operation={result} />}
                {result.result.length ? (
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: 'surface.raised',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      overflow: 'auto',
                    }}>
                    {JSON.stringify(result.result, null, 2)}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No result recorded yet.</Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close result</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
